'use client';

import {
  useAuth,
  useClerk,
  useOrganization,
  useOrganizationList,
} from '@clerk/clerk-react';
import { Building2, Check, ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Badge, Button } from '@/components/ui';
import { switchActiveOrganization } from '@/lib/organization/switchActiveOrganization';
import {
  isHealthy,
  useOrganizationsWithHealth,
} from '@/lib/query/useOrganizationsWithHealth';
import { cn } from '@/lib/utils';

/**
 * OrganizationSwitcher lets a user who belongs to more than one organization change the
 * active organization from the dashboard nav. Clerk stickily restores the last active organization on
 * sign-in without re-checking its health, so an organization whose GitHub App was removed (or
 * whose subscription was canceled) after selection can strand the user. The switcher
 * marks such organizations "Needs setup" so the user can move to a working organization without
 * operator surgery.
 *
 * The switchable organization list comes from Clerk memberships, not our DB mirror, so a
 * real Clerk organization never disappears from the picker just because its backend row is
 * missing or unhealthy. Backend health only annotates organizations that have a mirror row.
 *
 * Self-gates: a single-organization user has nothing to switch to, so it renders nothing.
 */
export function OrganizationSwitcher() {
  const { isLoaded: authLoaded, isSignedIn } = useAuth();
  const { setActive } = useClerk();
  const { organization: activeOrganization } = useOrganization();
  const { isLoaded, userMemberships } = useOrganizationList({
    userMemberships: { infinite: false },
  });
  const { data: organizations } = useOrganizationsWithHealth();

  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close the menu on an outside click while it is open.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const memberships = userMemberships?.data ?? [];
  const healthByClerkOrganizationId = new Map(
    (organizations ?? []).map((organization) => [
      organization.clerkOrganizationId,
      organization,
    ])
  );

  if (!authLoaded || !isSignedIn) {
    return null;
  }

  if (!isLoaded) {
    return (
      <Button
        type="button"
        variant="secondary"
        disabled
        aria-label="Loading organizations"
        className="max-w-[14rem] gap-2"
      >
        <Building2 className="h-4 w-4 shrink-0 opacity-70" />
        <span className="truncate">Organizations</span>
        <ChevronDown className="h-4 w-4 shrink-0 opacity-40" />
      </Button>
    );
  }

  // Nothing to switch between: stay out of the nav entirely.
  if (memberships.length <= 1) return null;

  const activeClerkOrganizationId = activeOrganization?.id;
  const activeName =
    memberships.find((m) => m.organization.id === activeClerkOrganizationId)
      ?.organization.name ??
    activeOrganization?.name ??
    'Select organization';

  const handleSelect = async (clerkOrganizationId: string) => {
    setOpen(false);
    if (clerkOrganizationId === activeClerkOrganizationId) return;
    await switchActiveOrganization(setActive, clerkOrganizationId);
  };

  return (
    <div ref={containerRef} className="relative">
      <Button
        type="button"
        variant="secondary"
        data-testid="organization-switcher-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="max-w-[14rem] gap-2"
      >
        <Building2 className="h-4 w-4 shrink-0 opacity-70" />
        <span className="truncate">{activeName}</span>
        <ChevronDown className="h-4 w-4 shrink-0 opacity-60" />
      </Button>

      {open && (
        <div
          role="menu"
          aria-label="Switch organization"
          className="bg-popover text-popover-foreground border-border absolute right-0 z-50 mt-2 w-64 overflow-hidden rounded-md border shadow-md"
        >
          {memberships.map((membership) => {
            const organization = membership.organization;
            const health = healthByClerkOrganizationId.get(organization.id);
            const isActive = organization.id === activeClerkOrganizationId;
            const needsSetup = health ? !isHealthy(health) : false;
            return (
              <button
                key={organization.id}
                type="button"
                role="menuitem"
                onClick={() => void handleSelect(organization.id)}
                className={cn(
                  'hover:bg-accent hover:text-accent-foreground flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-sm',
                  isActive && 'bg-accent/50'
                )}
              >
                <Check
                  className={cn(
                    'h-4 w-4 shrink-0',
                    isActive ? 'opacity-100' : 'opacity-0'
                  )}
                />
                <span className="flex-1 truncate">{organization.name}</span>
                {needsSetup && (
                  <Badge
                    variant="outline"
                    className="shrink-0 border-amber-500/50 bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
                  >
                    Needs setup
                  </Badge>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
